import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { UserPlus, ChevronDown, ChevronUp, Trophy, Sparkles, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((now - d) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/**
 * TopReferrersWidget — all-time leaderboard of who's sending Tides
 * business. Populated from the "Did someone refer you?" field on every
 * intake form (marketing site + SkyQuote). Shows top 5 collapsed with an
 * "Show all" toggle that expands to the full leaderboard. Each row is
 * clickable — jumps to the referrer's most recent project.
 */
export default function TopReferrersWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${API_URL}/analytics/top-referrers?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setData({ referrers: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="shadow-sm" data-testid="top-referrers-widget-loading">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#0F4C81]" />Top Referrers
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6 flex items-center justify-center text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading leaderboard…
        </CardContent>
      </Card>
    );
  }

  const rows = data?.referrers || [];
  if (rows.length === 0) {
    return (
      <Card className="shadow-sm" data-testid="top-referrers-widget-empty">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#0F4C81]" />Top Referrers
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-center text-slate-400 text-sm">
          No referrals yet. Once leads start coming in with "Did someone refer you?" filled in, they'll rank here.
        </CardContent>
      </Card>
    );
  }

  const visible = expanded ? rows : rows.slice(0, 5);
  const totalLeads = data?.total_referred_leads || 0;
  const totalRevenue = data?.total_referred_revenue || 0;

  // Deterministic medal color for top 3
  const medalColor = (idx) => {
    if (idx === 0) return "text-amber-500";     // gold
    if (idx === 1) return "text-slate-400";     // silver
    if (idx === 2) return "text-orange-600";    // bronze
    return "text-slate-300";
  };

  return (
    <Card className="shadow-sm" data-testid="top-referrers-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#0F4C81]" />Top Referrers
            <Badge variant="secondary" className="text-xs ml-1" data-testid="top-referrers-total-badge">
              {rows.length} {rows.length === 1 ? "person" : "people"}
            </Badge>
          </CardTitle>
          <div className="text-xs text-slate-500 tabular-nums flex items-center gap-3">
            <span>{totalLeads} lead{totalLeads === 1 ? "" : "s"}</span>
            {totalRevenue > 0 && (
              <span className="font-medium text-emerald-600">${totalRevenue.toLocaleString()}</span>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-1">All-time leaderboard, powered by the "Did someone refer you?" field on every intake form.</p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="divide-y divide-slate-100">
          {visible.map((r, idx) => {
            const closeRate = r.lead_count > 0 ? Math.round((r.signed_count / r.lead_count) * 100) : 0;
            const onOpen = () => {
              if (r.latest_project_id) navigate(`/projects/${r.latest_project_id}`);
            };
            return (
              <button
                key={r.name + idx}
                onClick={onOpen}
                className="w-full text-left flex items-center gap-3 py-2.5 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                data-testid={`top-referrer-row-${idx}`}
              >
                <div className="flex-shrink-0 w-6 flex items-center justify-center">
                  {idx < 3 ? (
                    <Trophy className={`w-4 h-4 ${medalColor(idx)}`} />
                  ) : (
                    <span className="text-xs font-semibold text-slate-400 tabular-nums">{idx + 1}</span>
                  )}
                </div>
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#0F4C81]/10 text-[#0F4C81] flex items-center justify-center">
                  <UserPlus className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-slate-800 text-sm truncate" data-testid={`top-referrer-name-${idx}`}>
                      {r.name}
                    </span>
                    {r.latest_referral_at && (
                      <span className="text-[11px] text-slate-400 flex-shrink-0">· latest {fmtDate(r.latest_referral_at)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 tabular-nums">
                    <span>
                      <span className="font-semibold text-slate-700">{r.lead_count}</span> lead{r.lead_count === 1 ? "" : "s"}
                    </span>
                    {r.signed_count > 0 && (
                      <span>
                        <span className="font-semibold text-emerald-600">{r.signed_count}</span> signed
                        <span className="text-slate-400"> · {closeRate}%</span>
                      </span>
                    )}
                    {r.signed_revenue > 0 && (
                      <span className="font-semibold text-emerald-600">${r.signed_revenue.toLocaleString()}</span>
                    )}
                  </div>
                </div>
                {idx === 0 && r.signed_count > 0 && (
                  <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" title="Top revenue driver" />
                )}
              </button>
            );
          })}
        </div>

        {rows.length > 5 && (
          <div className="pt-3 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="h-8 text-xs text-slate-500"
              data-testid="top-referrers-toggle"
            >
              {expanded ? (
                <>Show less <ChevronUp className="w-3.5 h-3.5 ml-1" /></>
              ) : (
                <>Show all {rows.length} <ChevronDown className="w-3.5 h-3.5 ml-1" /></>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
